import { c as _c } from "react/compiler-runtime";
import { useIsVisible } from "@website/hooks/is-visible";
import { useItemStagger } from "@website/widget/item-stagger/item-stagger.hook";
import { Fragment, useEffect, useRef, useState, type CSSProperties } from "react";
import { ListingBanners } from "./listing-banners";
import { ListingContainerBreakpoint, ListingContainerCols, ListingContainerPropertiesType, ListingContainerResponsiveCols } from "./listing-container.types";
import { ListingEmptyState } from "./listing-empty-state";
const GAP_X = {
  "4": "gap-x-4"
};
const GAP_Y = {
  "6": "gap-y-6"
};
const SKELETON_ITEMS_COUNT = 20;
const DEFAULT_PROGRESSIVE_BATCH_SIZE = 20;
const DEFAULT_PROGRESSIVE_ROOT_MARGIN = "400px 0px";
const OFFSCREEN_CARD_STYLE: CSSProperties = {
  containIntrinsicSize: "auto 340px",
  contentVisibility: "auto"
};
const colClasses: Record<ListingContainerBreakpoint, Record<ListingContainerCols, string>> = {
  default: {
    1: "grid-cols-1",
    2: "grid-cols-2",
    3: "grid-cols-3",
    4: "grid-cols-4",
    5: "grid-cols-5",
    6: "grid-cols-6"
  },
  sm: {
    1: "sm:grid-cols-1",
    2: "sm:grid-cols-2",
    3: "sm:grid-cols-3",
    4: "sm:grid-cols-4",
    5: "sm:grid-cols-5",
    6: "sm:grid-cols-6"
  },
  md: {
    1: "md:grid-cols-1",
    2: "md:grid-cols-2",
    3: "md:grid-cols-3",
    4: "md:grid-cols-4",
    5: "md:grid-cols-5",
    6: "md:grid-cols-6"
  },
  lg: {
    1: "lg:grid-cols-1",
    2: "lg:grid-cols-2",
    3: "lg:grid-cols-3",
    4: "lg:grid-cols-4",
    5: "lg:grid-cols-5",
    6: "lg:grid-cols-6"
  },
  xl: {
    1: "xl:grid-cols-1",
    2: "xl:grid-cols-2",
    3: "xl:grid-cols-3",
    4: "xl:grid-cols-4",
    5: "xl:grid-cols-5",
    6: "xl:grid-cols-6"
  }
};
function getGridClasses(cols: ListingContainerResponsiveCols) {
  return (Object.entries(cols) as Array<[ListingContainerBreakpoint, ListingContainerCols]>).map(([bp, value]) => colClasses[bp][value]).join(" ");
}
export function ListingContainer(t0) {
  const $ = _c(26);
  const {
    list,
    renderCard,
    renderSkeleton,
    isLoading,
    className: t1,
    gapX: t2,
    gapY: t3,
    cols: t4,
    getKey,
    banners,
    emptyState,
    shouldAnimate: t5,
    deferOffscreenRendering: t6,
    animationStartIndex: t7,
    progressiveRendering
  } = t0;
  const className = t1 === undefined ? "" : t1;
  const gapX = t2 === undefined ? "4" : t2;
  const gapY = t3 === undefined ? "6" : t3;
  let t8;
  if ($[0] !== t4) {
    t8 = t4 === undefined ? {
      default: 1,
      sm: 2,
      md: 2,
      lg: 3,
      xl: 4
    } : t4;
    $[0] = t4;
    $[1] = t8;
  } else {
    t8 = $[1];
  }
  const cols = t8;
  const shouldAnimate = t5 === undefined ? true : t5;
  const deferOffscreenRendering = t6 === undefined ? false : t6;
  const animationStartIndex = t7 === undefined ? 0 : t7;
  const progressiveBatchSize = progressiveRendering?.batchSize ?? DEFAULT_PROGRESSIVE_BATCH_SIZE;
  const [visibleItemCount, setVisibleItemCount] = useState(progressiveRendering ? progressiveBatchSize : list.length);
  const wasLoadMoreInViewReference = useRef(false);
  let t9;
  if ($[2] !== progressiveRendering) {
    t9 = progressiveRendering ? {
      rootMargin: progressiveRendering.rootMargin ?? DEFAULT_PROGRESSIVE_ROOT_MARGIN
    } : undefined;
    $[2] = progressiveRendering;
    $[3] = t9;
  } else {
    t9 = $[3];
  }
  const {
    reference: loadMoreReference,
    inView: isLoadMoreInView
  } = useIsVisible(t9, false, progressiveRendering?.scrollElementReference);
  const emptyStateProps = !isLoading && list.length === 0 ? emptyState : null;
  let t10;
  if ($[4] !== getKey) {
    t10 = (item, index) => {
      const key = getKey(item);
      return {
        staggerKey: `item-${String(key)}`,
        index,
        item,
        key
      };
    };
    $[4] = getKey;
    $[5] = t10;
  } else {
    t10 = $[5];
  }
  const listItems = list.map(t10);
  const visibleListItems = progressiveRendering ? listItems.slice(0, visibleItemCount) : listItems;
  const hasMoreItems = visibleListItems.length < listItems.length;
  const skeletonItems = isLoading ? Array.from({
    length: SKELETON_ITEMS_COUNT
  }).map((_, index_0) => ({
    index: index_0,
    staggerIndex: listItems.length + index_0,
    staggerKey: `skeleton-${index_0}`
  })) : [];
  const {
    getItemStaggerProperties
  } = useItemStagger(shouldAnimate ? [...visibleListItems, ...skeletonItems].map(_temp) : []);
  useEffect(() => {
    if (!isLoadMoreInView) {
      wasLoadMoreInViewReference.current = false;
      return;
    }
    if (wasLoadMoreInViewReference.current || !hasMoreItems) {
      return;
    }
    wasLoadMoreInViewReference.current = true;
    setVisibleItemCount(currentCount => Math.min(currentCount + progressiveBatchSize, listItems.length));
  }, [hasMoreItems, isLoadMoreInView, listItems.length, progressiveBatchSize]);
  let t11;
  if ($[6] !== cols) {
    t11 = getGridClasses(cols);
    $[6] = cols;
    $[7] = t11;
  } else {
    t11 = $[7];
  }
  const t12 = `grid ${t11} ${GAP_X[gapX]} ${GAP_Y[gapY]} ${className}`;
  let t13;
  if ($[8] !== emptyStateProps) {
    t13 = emptyStateProps && <ListingEmptyState {...emptyStateProps} />;
    $[8] = emptyStateProps;
    $[9] = t13;
  } else {
    t13 = $[9];
  }
  let t14;
  if ($[10] !== animationStartIndex || $[11] !== banners || $[12] !== deferOffscreenRendering || $[13] !== getItemStaggerProperties || $[14] !== renderCard || $[15] !== shouldAnimate) {
    t14 = t15 => {
      const {
        index: index_1,
        item: item_0,
        key: key_0,
        staggerKey: staggerKey_0
      } = t15;
      const itemProperties = shouldAnimate && index_1 >= animationStartIndex ? getItemStaggerProperties(staggerKey_0, index_1) : {
        className: "h-full min-w-0",
        style: undefined
      };
      return <Fragment key={`item-${String(key_0)}`}><ListingBanners banners={banners} index={index_1} /><div {...itemProperties} style={deferOffscreenRendering ? {
          ...itemProperties.style,
          ...OFFSCREEN_CARD_STYLE
        } : itemProperties.style}>{renderCard(item_0, index_1)}</div></Fragment>;
    };
    $[10] = animationStartIndex;
    $[11] = banners;
    $[12] = deferOffscreenRendering;
    $[13] = getItemStaggerProperties;
    $[14] = renderCard;
    $[15] = shouldAnimate;
    $[16] = t14;
  } else {
    t14 = $[16];
  }
  const t15 = visibleListItems.map(t14);
  const t17 = skeletonItems.map(t16 => {
    const {
      index: index_2,
      staggerIndex,
      staggerKey: staggerKey_1
    } = t16;
    return <div key={staggerKey_1} {...shouldAnimate && staggerIndex >= animationStartIndex ? getItemStaggerProperties(staggerKey_1, staggerIndex) : {
      className: "h-full min-w-0"
    }}>{renderSkeleton(index_2)}</div>;
  });
  let t18;
  if ($[17] !== hasMoreItems || $[18] !== loadMoreReference) {
    t18 = hasMoreItems && <div aria-hidden="true" className="col-span-full h-px w-full" ref={loadMoreReference} />;
    $[17] = hasMoreItems;
    $[18] = loadMoreReference;
    $[19] = t18;
  } else {
    t18 = $[19];
  }
  let t19;
  if ($[20] !== t12 || $[21] !== t13 || $[22] !== t15 || $[23] !== t17 || $[24] !== t18) {
    t19 = <section className={t12}>{t13}{t15}{t17}{t18}</section>;
    $[20] = t12;
    $[21] = t13;
    $[22] = t15;
    $[23] = t17;
    $[24] = t18;
    $[25] = t19;
  } else {
    t19 = $[25];
  }
  return t19;
}
function _temp(t0) {
  const {
    staggerKey
  } = t0;
  return staggerKey;
}